/**
 * @fileoverview Logout route component for handling post-logout redirection.
 *
 * Provides a landing page after Okta logout completion, clears user context
 * from error tracking, and redirects users back to the homepage.
 */

import { useOktaAuth } from '@okta/okta-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { clearSentryUserContext } from '../lib/sentry';

/**
 * TanStack Router configuration for the logout route.
 * Defines the /logout path with the LogoutPage component.
 */
export const Route = createFileRoute('/logout')({
  component: LogoutPage,
});

/**
 * Logout page component.
 *
 * Handles post-logout cleanup including clearing Sentry user context,
 * checking authentication state, and providing user feedback with
 * automatic redirection to homepage after a brief delay.
 */
function LogoutPage() {
  const { oktaAuth, authState } = useOktaAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Clear Sentry user context immediately when landing on logout page
    clearSentryUserContext();

    if (authState?.isAuthenticated) {
      // If still authenticated, it means logout hasn't completed or was initiated elsewhere.
      // This page is typically the redirect URI *after* Okta has logged the user out.
      // If we land here and are still authenticated, something is off, or we might trigger logout.
      // For now, we assume Okta handles its part and this is just the landing spot.
      console.log(
        'User is still authenticated on logout page, redirecting to clear session.'
      );
      // This might not be strictly necessary if Okta handles the full logout flow
      // and this URI is only for post-logout redirection.
      // oktaAuth.signOut({ postLogoutRedirectUri: import.meta.env.VITE_OKTA_LOGOUT_REDIRECT_URI });
    }
    // No specific action needed here for now as Okta handles the session termination.
    // This page primarily serves as a clean landing spot after Okta logout.
    // We can add a delay and then navigate to home, or just show a message.

    setTimeout(() => {
      navigate({ to: '/' });
    }, 3000); // Redirect to home after 3 seconds
  }, [oktaAuth, authState, navigate]);

  return (
    <div className='p-4' aria-live='polite' aria-atomic='true'>
      <h1 className='text-xl font-semibold'>Logout</h1>
      <p>You have been logged out or are being logged out.</p>
      <p>
        You will be redirected shortly, or you can{' '}
        <a href='/' className='underline'>
          return to the homepage
        </a>
        .
      </p>
    </div>
  );
}
